import { useRouter, useRouterState } from '@tanstack/react-router';
import { useMemo } from 'react';
import type { BreadcrumbTrailItem } from '@/components/shared/AppHeader/types';
import { useBreadcrumbStore } from '@/store/breadcrumbStore';
import { useFeatures } from './useFeatures';
import { useTranslator } from './useTranslator';

function toLabel(segment: string) {
  return segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * The pathname is percent-encoded, router params are not.
 *
 * An id containing anything URL-unsafe — a platform feature is
 * `automation-rules:nl-draft`, which encodes to `automation-rules%3Anl-draft` —
 * would otherwise match neither the route's param values nor a registered name
 * override, and reach the crumb as the title-cased escape sequence
 * ("Automation-rules%3Anl-draft").
 *
 * Only the label and the id/override lookups use the decoded form; hrefs keep
 * the raw segment, since that is what the router has to navigate to.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // Malformed escape (a bare `%`) — the raw segment is the best we have.
    return segment;
  }
}

/**
 * Does `segments` satisfy a route pattern such as
 * `['my-components', '$kind', '$id', 'documents']`? A `$`-prefixed pattern
 * segment stands for a param and matches anything.
 */
function matchesRoutePattern(pattern: string[], segments: string[]) {
  if (pattern.length !== segments.length) return false;
  return pattern.every((p, i) => p.startsWith('$') || p === segments[i]);
}

/**
 * Route params whose value is human-readable and belongs in the trail
 * (`/docs/components/status-badge`, `/report/…/drilldown/$chartType`).
 * Every other param is treated as an opaque id and hidden — new id-shaped
 * params are therefore hidden by default, which is the behaviour we want:
 * a raw id must never reach the breadcrumb.
 */
const LABEL_PARAMS = new Set(['slug', 'kind', 'chartType']);

/**
 * Shape-based fallback for segments the router doesn't report as params
 * (e.g. a `*` splat route). Params are the authority — this only catches
 * leftovers.
 */
function looksLikeId(segment: string) {
  // UUID
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    )
  ) {
    return true;
  }
  // Pure numeric id
  if (/^\d+$/.test(segment)) return true;
  // cuid / nanoid / prefixed ULIDs (e.g. `style_01KKXW391NZT89S2TS4V0E5AKH`)
  // and similar opaque IDs — long alphanumeric strings (underscore allowed)
  // that contain at least one digit. The digit requirement prevents normal
  // page names (`variants`, `products`, `settings`, `my_styles`, etc.) from
  // being mistaken for IDs and stripped from the breadcrumb trail.
  return /^[0-9a-zA-Z_]{8,}$/.test(segment) && /\d/.test(segment);
}

/**
 * Translation key for the trailing crumb of a detail route (`…/$id`).
 * Keyed by the section segment that precedes the id. Defaults to
 * `pages.view`; add an entry here only when a section needs a different
 * verb (e.g. `network: 'pages.profile'`).
 */
const DETAIL_VERB_KEY: Record<string, string> = {};

export function useBreadcrumbTrail(): BreadcrumbTrailItem[] {
  const router = useRouter();
  // Use resolvedLocation so breadcrumb updates only after loader completes.
  const pathname =
    useRouterState({
      select: s => s.resolvedLocation?.pathname ?? s.location.pathname,
    }) ?? '/';
  // Values of the matched route's own params — the authoritative list of
  // "this segment is data, not a page name". Joined into a string so the
  // selector stays referentially stable across renders.
  const paramValuesKey = useRouterState({
    select: s => {
      const values: string[] = [];
      for (const match of s.matches) {
        for (const [name, value] of Object.entries(match.params ?? {})) {
          if (LABEL_PARAMS.has(name)) continue;
          if (typeof value === 'string' && value) values.push(value);
        }
      }
      return values.join('\0');
    },
  });
  const paramValues = useMemo(
    () => new Set(paramValuesKey ? paramValuesKey.split('\0') : []),
    [paramValuesKey],
  );
  // Every route the app declares, as segment patterns — the authority on
  // whether a breadcrumb href actually leads somewhere.
  const routePatterns = useMemo(
    () =>
      Object.keys(router.routesByPath).map(p => p.split('/').filter(Boolean)),
    [router],
  );
  const { getTranslation: t, i18n } = useTranslator({ page: 'appHeader' });
  const { isOrdersHubEnabled } = useFeatures();
  // Per-segment name overrides registered by detail routes (id → name).
  const overrides = useBreadcrumbStore(s => s.overrides);

  // A segment with a registered name is never treated as an opaque id —
  // it's shown with its human label.
  const isIdSegment = (segment: string) =>
    (paramValues.has(segment) || looksLikeId(segment)) && !overrides[segment];

  function translateSegment(segment: string): string {
    if (overrides[segment]) return overrides[segment];
    const key = hubPageKey(segment) ?? `pages.${segment}`;
    const translated = t(key);
    // t() returns the key itself when missing — fall back to toLabel
    return translated !== key ? translated : toLabel(segment);
  }

  // With the Order Hub, tracing pages read as requests (`pages.*-hub`).
  // `pages.orders-hub` is the Order Hub's own page, so `orders` has its own key.
  function hubPageKey(segment: string): string | undefined {
    if (!isOrdersHubEnabled) return undefined;
    if (segment === 'orders') return 'pages.tracing-requests';
    const hubKey = `pages.${segment}-hub`;
    return i18n.exists(hubKey, { ns: 'appHeader' }) ? hubKey : undefined;
  }

  if (pathname === '/') return [{ label: t('pages.dashboard') }];

  const rawSegments = pathname.split('/').filter(Boolean);
  const decodedSegments = rawSegments.map(decodeSegment);
  // Hidden id segments still count towards the href — only the *label* is
  // dropped. `position` is the index in `rawSegments`, so a crumb links to
  // the path the user is actually looking at, ids and all.
  const crumbSegments = decodedSegments
    .map((segment, position) => ({ segment, position }))
    .filter(s => !isIdSegment(s.segment));

  if (crumbSegments.length === 0) {
    const last = decodedSegments[decodedSegments.length - 1] ?? '';
    return [{ label: translateSegment(last) }];
  }

  // A prefix is only a link when it is a real route. `/my-components/styles`
  // (a bare `$kind` with no `$id`) is a path the app can build but not
  // navigate to — linking it lands the user on a Not Found page.
  const hrefAt = (position: number): string | undefined => {
    const path = `/${rawSegments.slice(0, position + 1).join('/')}`;
    const parts = path.split('/').filter(Boolean);
    return routePatterns.some(p => matchesRoutePattern(p, parts))
      ? path
      : undefined;
  };

  const crumbAt = ({
    segment,
    position,
  }: {
    segment: string;
    position: number;
  }): BreadcrumbTrailItem => {
    const href = hrefAt(position);
    return {
      label: translateSegment(segment),
      ...(href !== undefined ? { href } : {}),
    };
  };

  // Detail route — path ends with an id. Show the action verb (default
  // "View") instead of fetching the entity name.
  const lastRaw = decodedSegments[decodedSegments.length - 1] ?? '';
  if (isIdSegment(lastRaw) && !crumbSegments.some(s => s.segment === lastRaw)) {
    const section = crumbSegments[crumbSegments.length - 1]?.segment ?? '';
    const verbKey = DETAIL_VERB_KEY[section] ?? 'pages.view';
    return [...crumbSegments.map(crumbAt), { label: t(verbKey) }];
  }

  if (crumbSegments.length === 1)
    return [{ label: translateSegment(crumbSegments[0]?.segment ?? '') }];

  const crumbs = crumbSegments.map((s, i) =>
    // The trailing crumb is the current page — never a link.
    i < crumbSegments.length - 1
      ? crumbAt(s)
      : { label: translateSegment(s.segment) },
  );

  // Collapse adjacent crumbs that translate to the same label (e.g. a
  // bare-section route that redirects into a same-named child segment,
  // like /network → /network/my-network) — show it once, and make sure
  // the surviving trailing crumb has no href (it's the current page).
  const deduped = crumbs.filter(
    (c, i) => i === 0 || c.label !== crumbs[i - 1]?.label,
  );
  return deduped.map((c, i) =>
    i === deduped.length - 1 ? { label: c.label } : c,
  );
}
